"use client";

import React, { Component, ErrorInfo, ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

// #region React error decoder
const REACT_ERROR_MESSAGES: Record<number, string> = {
  130: "Element type is invalid: expected a string (for built-in components) or a class/function (for composite components) but got: %s. You likely forgot to export your component from the file it's defined in, or you might have mixed up default and named imports.",
  152: "Nothing was returned from render. This usually means a return statement is missing.",
  321: "Objects are not valid as a React child. If you meant to render a collection of children, use an array instead.",
};

function decodeReactError(message: string): string {
  const match = message.match(/Minified React error #(\d+)/);
  if (match) {
    const errorCode = parseInt(match[1], 10);
    const argsMatch = message.match(/args\[\]=([^&]+)/g);
    const args =
      argsMatch?.map((a) => decodeURIComponent(a.split("=")[1])) || [];

    let decoded =
      REACT_ERROR_MESSAGES[errorCode] || `Unknown React error #${errorCode}`;
    args.forEach((arg) => {
      decoded = decoded.replace("%s", arg);
    });

    return `React Error #${errorCode}:\n${decoded}\n\nArgs: ${args.join(", ")}`;
  }
  return message;
}
// #endregion

/**
 * Development Error Boundary - Detailed error page for debugging
 * Displays comprehensive error information including stack traces and component stacks
 */
export class DevelopmentErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  public static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("=== Development Error Boundary Caught ===");
    console.error("Error:", error);
    console.error("Error message:", error.message);
    console.error("Decoded message:", decodeReactError(error.message));
    console.error("Error stack:", error.stack);
    console.error("Component stack:", errorInfo.componentStack);

    this.setState({ errorInfo });
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleCopyError = () => {
    const errorText = `
Error: ${this.state.error?.message}
Stack: ${this.state.error?.stack}
Component Stack: ${this.state.errorInfo?.componentStack}
    `.trim();

    navigator.clipboard.writeText(errorText).then(() => {
      alert("Error details copied to clipboard!");
    });
  };

  public render() {
    if (this.state.hasError) {
      const decodedMessage = this.state.error?.message
        ? decodeReactError(this.state.error.message)
        : "";

      return (
        <div className="min-h-screen w-full bg-red-50 p-4 font-mono">
          <div className="mx-auto max-w-6xl">
            {/* Header */}
            <div className="mb-6 rounded-lg bg-red-600 p-4 text-white shadow-lg">
              <div className="flex items-center gap-3">
                <svg
                  className="h-8 w-8"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
                <h1 className="text-2xl font-bold">
                  🚨 Development Error Boundary
                </h1>
              </div>
              <p className="mt-2 text-sm text-red-100">
                This is the development error page. Check the details below to
                debug the issue.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="mb-4 flex flex-wrap gap-2">
              <button
                onClick={this.handleReload}
                className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
              >
                Reload Page
              </button>
              <button
                onClick={this.handleCopyError}
                className="rounded bg-gray-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-gray-700"
              >
                Copy Error Details
              </button>
            </div>

            {/* Decoded Error */}
            {decodedMessage && (
              <div className="mb-4 rounded-lg bg-white p-4 shadow-md">
                <h2 className="mb-2 text-lg font-bold text-red-600">
                  Decoded Error:
                </h2>
                <pre className="whitespace-pre-wrap break-words rounded bg-red-50 p-3 text-sm">
                  {decodedMessage}
                </pre>
              </div>
            )}

            {/* Raw Error Message */}
            {this.state.error?.message && (
              <div className="mb-4 rounded-lg bg-white p-4 shadow-md">
                <h2 className="mb-2 text-lg font-bold text-orange-600">
                  Raw Error Message:
                </h2>
                <pre className="whitespace-pre-wrap break-words text-xs">
                  {this.state.error.message}
                </pre>
              </div>
            )}

            {/* Error Stack */}
            {this.state.error?.stack && (
              <div className="mb-4 rounded-lg bg-white p-4 shadow-md">
                <h2 className="mb-2 text-lg font-bold text-orange-600">
                  Error Stack:
                </h2>
                <pre className="max-h-96 overflow-auto whitespace-pre-wrap break-words text-xs text-gray-700">
                  {this.state.error.stack}
                </pre>
              </div>
            )}

            {/* Component Stack */}
            {this.state.errorInfo?.componentStack && (
              <div className="mb-4 rounded-lg bg-white p-4 shadow-md">
                <h2 className="mb-2 text-lg font-bold text-blue-600">
                  Component Stack (find the culprit here!):
                </h2>
                <pre className="max-h-96 overflow-auto whitespace-pre-wrap break-words rounded bg-blue-50 p-3 text-xs text-gray-700">
                  {this.state.errorInfo.componentStack}
                </pre>
              </div>
            )}

            {/* Debug Tips */}
            <div className="rounded-lg bg-yellow-50 border border-yellow-200 p-4">
              <h3 className="mb-2 font-bold text-yellow-800">Debug Tips:</h3>
              <ul className="list-inside list-disc space-y-1 text-sm text-yellow-700">
                <li>
                  Check the Component Stack above to find which component is
                  invalid
                </li>
                <li>Check browser console (F12) for additional debug logs</li>
                <li>Verify component imports and exports</li>
                <li>Check for missing return statements in render methods</li>
                <li>Ensure all React hooks are used correctly</li>
              </ul>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

